import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'

export type SsoProvider = 'muni' | 'eduid' | 'google' | 'microsoft' | 'apple' | 'seznam'

export interface SsoProvidersProps {
	/** Per-provider flags from /api/auth/config; a disabled provider is not rendered. */
	enabled: Record<SsoProvider, boolean>
	/** The provider currently mid-redirect, or null. Only one can run at a time. */
	loading: SsoProvider | null
	/** True while the email/password form is submitting — blocks SSO as well. */
	busy?: boolean
	onSelect: (provider: SsoProvider) => void
}

/**
 * Official "M" mark from the MUNI single-sign-on branding kit
 * (it.muni.cz → Jednotné přihlášení na MUNI → branding, `svg/M_logo.svg`).
 * The kit ships two buttons: dark (solid #0000DC, mark and label white) and
 * light (white, mark and label #0000DC). The source rect is dropped and only
 * the letter is drawn in currentColor, so the button's text colour picks the
 * variant — light for the light theme, dark for the dark theme (MU, 24. 8.).
 */
function MuniMark() {
	return (
		<svg viewBox="0 0 19 19" width="22" height="22" aria-hidden="true">
			<path
				d="M5.473,3.323l0,11.842l1.877,-0l0,-11.842l-1.876,0Zm1.945,0l1.172,11.842l0.639,-0l-1.173,-11.842l-0.638,0Zm3.052,0l-1.172,11.842l0.637,-0l1.172,-11.842l-0.637,0Zm0.706,0l0,11.842l1.894,-0l0,-11.842l-1.894,0Z"
				fill="currentColor"
				fillRule="nonzero"
			/>
		</svg>
	)
}

/** Google's four-colour "G". Monochrome or recoloured variants are not allowed. */
function GoogleMark() {
	return (
		<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
			<path
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
				fill="#4285F4"
			/>
			<path
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
				fill="#34A853"
			/>
			<path
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
				fill="#FBBC05"
			/>
			<path
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
				fill="#EA4335"
			/>
		</svg>
	)
}

function MicrosoftMark() {
	return (
		<svg viewBox="0 0 23 23" width="18" height="18" aria-hidden="true">
			<rect x="1" y="1" width="10" height="10" fill="#F25022" />
			<rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
			<rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
			<rect x="12" y="12" width="10" height="10" fill="#FFB900" />
		</svg>
	)
}

function AppleMark() {
	return (
		<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
			<path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z" />
			<path d="M15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z" />
		</svg>
	)
}

function SeznamMark() {
	return (
		<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
			<rect x="1" y="1" width="22" height="22" rx="5" fill="#CC0000" />
			<text
				x="12"
				y="12.5"
				textAnchor="middle"
				dominantBaseline="central"
				fontFamily="ui-sans-serif, system-ui, sans-serif"
				fontSize="14"
				fontWeight="700"
				fill="#FFFFFF"
			>
				S
			</text>
		</svg>
	)
}

/** Square icon-only button: 48px hit target, label carried by aria-label + title. */
const iconButtonClass = 'h-12 w-12 shrink-0 rounded-xl p-0'

/**
 * Identity providers on the sign-in page, ordered and styled the way MU asked
 * (feedback 2026-07, "povinná grafika poskytovatelů identit"): the academic
 * providers come first as full-width buttons, the commercial ones sit below in
 * a compact row.
 *
 * Each provider follows its own brand rules, which are not the same:
 * - MUNI publishes a branding kit; this is its dark button rebuilt in CSS.
 * - eduID.cz publishes a logo but no button guidance, so MU asked for it to be
 *   handled as plain text — the label just has to read "eduID.cz".
 * - Google permits the bare "G" as long as it keeps a button boundary.
 * - Apple lists a logo-only button as an official variant (black or white, 1:1).
 * - Microsoft does NOT permit a logo-only button: its guidelines require the
 *   logo locked up with text, the shortest sanctioned form being logo +
 *   "Sign in". We show the icon alone anyway — a deliberate call by the product
 *   owner (2026-08-23) to keep the row uniform. Do not "fix" this back to a
 *   text lockup without asking; the deviation is known and intended.
 */
export function SsoProviders({ enabled, loading, busy = false, onSelect }: SsoProvidersProps) {
	const { t } = useTranslation()
	const anyBusy = loading !== null || busy
	const hasCommercial = enabled.google || enabled.microsoft || enabled.apple || enabled.seznam

	return (
		<>
			{enabled.muni && (
				<Button
					onClick={() => onSelect('muni')}
					disabled={anyBusy}
					loading={loading === 'muni'}
					leadingIcon={
						loading !== 'muni' && (
							<>
								<MuniMark />
								<span aria-hidden="true" className="h-6 w-px bg-current opacity-60" />
							</>
						)
					}
					variant="ghost"
					block
					// `!` because the brand colours must beat the variant's own
					// background/hover utilities, which Tailwind may emit later.
					// The theme lives in `data-theme` on <html> (App), not in a
					// `dark` class, so the dark variant is selected by attribute.
					className="mb-3 border border-[#0000DC] !bg-white !text-[#0000DC] hover:!brightness-95 [[data-theme=polygraf-dark]_&]:!bg-[#0000DC] [[data-theme=polygraf-dark]_&]:!text-white [[data-theme=polygraf-dark]_&]:hover:!brightness-110"
				>
					{loading === 'muni' ? t('auth.redirecting') : t('auth.loginWithMuni')}
				</Button>
			)}

			{/* eduID covers any other Czech university federated in eduID.cz; only
				visible when the SimpleSAMLphp bridge is deployed and configured. */}
			{enabled.eduid && (
				<Button
					onClick={() => onSelect('eduid')}
					disabled={anyBusy}
					loading={loading === 'eduid'}
					variant="ghost"
					block
					className="mb-4 border border-base-300"
				>
					{loading === 'eduid' ? t('auth.redirecting') : t('auth.loginWithEduid')}
				</Button>
			)}

			{hasCommercial && (
				<>
					<div className="divider text-base-content/40 text-sm mb-4">
						{t('auth.orContinueWith')}
					</div>

					<div className="flex flex-wrap items-center justify-center gap-3 mb-5">
						{enabled.google && (
							<Button
								onClick={() => onSelect('google')}
								disabled={anyBusy}
								loading={loading === 'google'}
								leadingIcon={loading !== 'google' && <GoogleMark />}
								variant="ghost"
								aria-label={t('auth.loginWithGoogle')}
								title={t('auth.loginWithGoogle')}
								className={`${iconButtonClass} border border-base-300 bg-white`}
							/>
						)}

						{enabled.apple && (
							<Button
								onClick={() => onSelect('apple')}
								disabled={anyBusy}
								loading={loading === 'apple'}
								leadingIcon={loading !== 'apple' && <AppleMark />}
								variant="ghost"
								aria-label={t('auth.loginWithApple')}
								title={t('auth.loginWithApple')}
								className={`${iconButtonClass} border border-base-300 bg-white text-black`}
							/>
						)}

						{enabled.seznam && (
							<Button
								onClick={() => onSelect('seznam')}
								disabled={anyBusy}
								loading={loading === 'seznam'}
								leadingIcon={loading !== 'seznam' && <SeznamMark />}
								variant="ghost"
								aria-label={t('auth.loginWithSeznam')}
								title={t('auth.loginWithSeznam')}
								className={`${iconButtonClass} border border-base-300 bg-white`}
							/>
						)}

						{enabled.microsoft && (
							<Button
								onClick={() => onSelect('microsoft')}
								disabled={anyBusy}
								loading={loading === 'microsoft'}
								leadingIcon={loading !== 'microsoft' && <MicrosoftMark />}
								variant="ghost"
								aria-label={t('auth.loginWithMicrosoft')}
								title={t('auth.loginWithMicrosoft')}
								className={`${iconButtonClass} border border-base-300 bg-white`}
							/>
						)}
					</div>
				</>
			)}
		</>
	)
}
